/**
 * Header Component
 * Dashboard header with logo, title, status, and controls
 * Redesigned for clarity and premium UX
 */

import React, { useState, useEffect } from 'react';
import { Settings, RefreshCw, Brain } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';
import { useAPI } from '../hooks/useAPI';
import { SettingsModal } from './SettingsModal';

const STATUS_CONFIG = {
  idle: {
    label: 'Standby',
    description: 'Waiting to connect',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100/20 border-gray-400/30 text-gray-200',
    pulse: false,
  },
  connecting: {
    label: 'Connecting',
    description: 'Establishing connection…',
    dot: 'bg-amber-400',
    badge: 'bg-amber-400/20 border-amber-300/40 text-amber-200',
    pulse: true,
  },
  connected: {
    label: 'Live',
    description: 'System is running',
    dot: 'bg-emerald-400',
    badge: 'bg-emerald-400/20 border-emerald-300/40 text-emerald-200',
    pulse: true,
  },
  disconnected: {
    label: 'Offline',
    description: 'Connection lost',
    dot: 'bg-red-400',
    badge: 'bg-red-400/20 border-red-400/40 text-red-200',
    pulse: false,
  },
};

const MCP_STATUS_CONFIG = {
  active: {
    label: 'MCP: Active',
    dot: 'bg-cyan-500',
    pulse: true,
    badge: 'bg-cyan-500/20 border-cyan-400/40 text-cyan-200',
  },
  idle: {
    label: 'MCP: Idle',
    dot: 'bg-gray-500',
    pulse: false,
    badge: 'bg-gray-500/20 border-gray-400/40 text-gray-300',
  },
  offline: {
    label: 'MCP: Offline',
    dot: 'bg-red-500',
    pulse: false,
    badge: 'bg-red-500/20 border-red-400/40 text-red-300',
  },
};

export const Header: React.FC = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [mcpStatus, setMcpStatus] = useState<'active' | 'idle' | 'offline'>('offline');
  const connectionStatus = useDashboardStore((state) => state.connectionStatus);
  const metrics = useDashboardStore((state) => state.metrics);
  const { clearCache } = useAPI({ enableSocket: false });

  // Poll MCP status every 5 seconds
  useEffect(() => {
    const checkMcpStatus = async () => {
      try {
        const res = await fetch('/api/mcp-status');
        const data = await res.json();
        setMcpStatus(data.status || 'offline');
      } catch {
        setMcpStatus('offline');
      }
    };
    
    checkMcpStatus();
    const interval = setInterval(checkMcpStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const status = STATUS_CONFIG[connectionStatus];
  const mcpConfig = MCP_STATUS_CONFIG[mcpStatus];

  return (
    <>
      <header className="bg-slate-900/60 backdrop-blur-xl px-6 py-3 border-b border-white/5 relative overflow-hidden flex-shrink-0">
        <div className="flex items-center justify-between relative">
          {/* Brand */}
          <div className="flex items-center gap-4">
            <div className="w-9 h-9 bg-indigo-600/20 backdrop-blur rounded-lg flex items-center justify-center border border-indigo-500/30">
              <Brain className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-lg font-black text-white tracking-tight leading-none uppercase italic">
                Lemma <span className="text-indigo-500 font-bold not-italic text-sm ml-1">v0.7.8</span>
              </h1>
              <p className="text-[10px] text-slate-500 mt-1 font-bold uppercase tracking-widest">
                Semantic Cache & AI Orchestrator
              </p>
            </div>

            {/* Divider */}
            <div className="hidden sm:block h-6 w-px bg-white/5 mx-2" />

            {/* System status badge */}
            <div className={`hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border ${status.badge} backdrop-blur-sm`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status.dot} ${status.pulse ? 'animate-pulse' : ''}`} />
              <span className="text-[10px] font-black tracking-widest uppercase">{status.label}</span>
            </div>

            {/* MCP status badge */}
            <div className={`hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border ${mcpConfig.badge} backdrop-blur-sm`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${mcpConfig.dot} ${mcpConfig.pulse ? 'animate-pulse' : ''}`} />
              <span className="text-[10px] font-black tracking-widest uppercase">{mcpConfig.label}</span>
            </div>

            <div className="flex items-center gap-1 ml-4">
              <button 
                onClick={() => useDashboardStore.getState().setActiveSidePanel('agents')}
                className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-md transition-all ${
                  useDashboardStore((state) => state.activeSidePanel === 'agents') 
                    ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' 
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                The Hub
              </button>
              <button 
                onClick={() => useDashboardStore.getState().setActiveSidePanel('tools')}
                className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-md transition-all ${
                  useDashboardStore((state) => state.activeSidePanel === 'tools') 
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                The Tools
              </button>
              <button 
                onClick={() => useDashboardStore.getState().setActiveSidePanel('brain')}
                className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-md transition-all ${
                  useDashboardStore((state) => state.activeSidePanel === 'brain') 
                    ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' 
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                The Brain
              </button>
            </div>
          </div>

          {/* Tier Badge */}
          <div className="flex items-center gap-3">
            {metrics?.tier === 'pro' ? (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                <span className="text-[10px] font-black uppercase tracking-widest text-violet-300">PRO</span>
              </div>
            ) : metrics ? (
              <a
                href="https://lemma.nxus.studio/upgrade"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-full transition-all duration-200 animate-pulse-neon"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Upgrade to Pro · $12/mo
              </a>
            ) : null}

            <button
              onClick={() => clearCache()}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-white/5 hover:bg-white/10 border border-white/5 rounded-md transition-all duration-200"
            >
              <RefreshCw className="w-3 h-3" />
              Flush Cache
            </button>

            <button
              onClick={() => setIsSettingsOpen(true)}
              className="p-2 text-slate-400 bg-white/5 hover:bg-white/10 border border-white/5 rounded-md transition-all duration-200"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </>
  );
};
